import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Calendar, Plus, Edit2, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LeaveTypesMaster({ user, onLogout }) {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    total_days: 12,
    carry_forward: true,
    max_carry_forward: 5,
    encashment_allowed: false,
    requires_medical_cert: false,
    min_notice_days: 1,
    max_consecutive_days: 10,
    applicable_to: ['Full-time']
  });

  const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Intern'];

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  const fetchLeaveTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/leave-balance/leave-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveTypes(response.data.leave_types || []);
    } catch (error) {
      console.error('Failed to fetch leave types:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingType) {
        await axios.put(`${API}/leave-balance/leave-types/${editingType.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Leave type updated successfully!');
      } else {
        await axios.post(`${API}/leave-balance/leave-types`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Leave type created successfully!');
      }

      resetForm();
      fetchLeaveTypes();
    } catch (error) {
      alert('Failed to save leave type: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      total_days: type.total_days,
      carry_forward: type.carry_forward,
      max_carry_forward: type.max_carry_forward || 5,
      encashment_allowed: type.encashment_allowed,
      requires_medical_cert: type.requires_medical_cert,
      min_notice_days: type.min_notice_days || 1,
      max_consecutive_days: type.max_consecutive_days || 10,
      applicable_to: type.applicable_to || ['Full-time']
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this leave type?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/leave-balance/leave-types/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Leave type deleted successfully!');
      fetchLeaveTypes();
    } catch (error) {
      alert('Failed to delete leave type: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      total_days: 12,
      carry_forward: true,
      max_carry_forward: 5,
      encashment_allowed: false,
      requires_medical_cert: false,
      min_notice_days: 1,
      max_consecutive_days: 10,
      applicable_to: ['Full-time']
    });
    setEditingType(null);
    setShowForm(false);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Leave Types Master</h1>
            <p className="text-gray-600 mt-1">Configure leave categories and policies</p>
          </div>
          <button
            onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }}
            className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
            style={{backgroundColor: '#2b3380'}}
          >
            <Plus className="w-5 h-5" />
            {showForm ? 'Cancel' : 'Add Leave Type'}
          </button>
        </div>

        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>
              {editingType ? 'Edit Leave Type' : 'Add New Leave Type'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Leave Type Name *</label>
                  <input
                    type="text"
                    placeholder="e.g., Annual Leave, Sick Leave"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Total Days Per Year *</label>
                  <input
                    type="number"
                    value={formData.total_days}
                    onChange={(e) => setFormData({...formData, total_days: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Max Carry Forward Days</label>
                  <input
                    type="number"
                    value={formData.max_carry_forward}
                    onChange={(e) => setFormData({...formData, max_carry_forward: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Min Notice Days</label>
                  <input
                    type="number"
                    value={formData.min_notice_days}
                    onChange={(e) => setFormData({...formData, min_notice_days: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Max Consecutive Days</label>
                  <input
                    type="number"
                    value={formData.max_consecutive_days}
                    onChange={(e) => setFormData({...formData, max_consecutive_days: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="0"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Applicable To</label>
                  <div className="flex gap-4">
                    {EMPLOYMENT_TYPES.map(type => (
                      <label key={type} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.applicable_to.includes(type)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({...formData, applicable_to: [...formData.applicable_to, type]});
                            } else {
                              setFormData({...formData, applicable_to: formData.applicable_to.filter(t => t !== type)});
                            }
                          }}
                        />
                        {type}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.carry_forward}
                      onChange={(e) => setFormData({...formData, carry_forward: e.target.checked})}
                    />
                    Carry Forward
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.encashment_allowed}
                      onChange={(e) => setFormData({...formData, encashment_allowed: e.target.checked})}
                    />
                    Encashment Allowed
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.requires_medical_cert}
                      onChange={(e) => setFormData({...formData, requires_medical_cert: e.target.checked})}
                    />
                    Medical Certificate
                  </label>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-6 py-2 rounded-lg text-white"
                  style={{backgroundColor: '#2b3380'}}
                >
                  {editingType ? 'Update' : 'Create'}
                </button>
                <button type="button" onClick={resetForm} className="px-6 py-2 rounded-lg border">Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {leaveTypes.length === 0 ? (
            <div className="col-span-full bg-white rounded-lg shadow-md p-8 text-center">
              <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p className="text-gray-500">No leave types configured yet</p>
            </div>
          ) : (
            leaveTypes.map((type) => (
              <div key={type.id} className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-bold" style={{color: '#2b3380'}}>{type.name}</h3>
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {type.total_days} days
                  </span>
                </div>

                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Carry Forward:</span>
                    <span className="font-medium">{type.carry_forward ? `${type.max_carry_forward} days` : 'No'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Encashment:</span>
                    <span className="font-medium">{type.encashment_allowed ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Min Notice:</span>
                    <span className="font-medium">{type.min_notice_days} days</span>
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(type)}
                    className="flex-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center justify-center gap-1"
                  >
                    <Edit2 className="w-3 h-3" />Edit
                  </button>
                  <button
                    onClick={() => handleDelete(type.id)}
                    className="flex-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center justify-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" />Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}

export default LeaveTypesMaster;